import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  message,
  Table,
  Tag,
  Space,
  Modal,
  Input,
  Form,
  Statistic,
  Row,
  Col,
  Tooltip,
  Typography,
  DatePicker,
  Descriptions,
  Empty
} from 'antd';
import {
  GlobalOutlined,
  ReloadOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MailOutlined,
  SearchOutlined,
  BarChartOutlined,
  CloudServerOutlined,
  UserOutlined
} from '@ant-design/icons';
import API from '../services/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';
import './AdminDomainWhitelistManagement.css';

dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { TextArea } = Input;

const AdminDomainWhitelistManagement = () => {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  // 筛选条件
  const [searchTerm, setSearchTerm] = useState('');
  const [serverIPFilter, setServerIPFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);

  // 统计数据
  const [statistics, setStatistics] = useState({
    totalRequests: 0,
    emailSentCount: 0,
    emailFailedCount: 0,
    totalDomains: 0,
    dailyTrend: []
  });

  // Modal状态
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [statisticsModalVisible, setStatisticsModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [createForm] = Form.useForm();

  // 获取域名过白申请列表
  const fetchRequests = async (page = pagination.current, limit = pagination.pageSize) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        search: searchTerm,
        serverIP: serverIPFilter
      };

      if (dateRange && dateRange.length === 2) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }

      const response = await API.get('/api/admin/domain-whitelist-requests', { params });

      if (response.data.success) {
        setRequests(response.data.data.list);
        setPagination({
          current: response.data.data.page,
          pageSize: response.data.data.limit,
          total: response.data.data.total
        });
      } else {
        message.error(response.data.message || '获取域名过白申请记录失败');
      }
    } catch (error) {
      console.error('获取域名过白申请记录错误:', error);
      message.error(error.response?.data?.message || '获取域名过白申请记录失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取统计数据
  const fetchStatistics = async () => {
    try {
      const response = await API.get('/api/admin/domain-whitelist-requests/statistics');

      if (response.data.success) {
        setStatistics(response.data.data);
      } else {
        message.error(response.data.message || '获取统计数据失败');
      }
    } catch (error) {
      console.error('获取统计数据错误:', error);
      message.error(error.response?.data?.message || '获取统计数据失败');
    }
  };

  // 提交域名过白申请
  const handleCreateRequest = async (values) => {
    try {
      // 将域名文本拆分为数组
      const domains = values.domains
        .split(/[\n,，\s]+/)
        .map(d => d.trim())
        .filter(d => d);

      const response = await API.post('/api/admin/domain-whitelist-requests', {
        serverIP: values.serverIP,
        domains
      });

      if (response.data.success) {
        message.success(response.data.message || '域名过白申请提交成功');
        setCreateModalVisible(false);
        createForm.resetFields();
        fetchRequests();
        fetchStatistics();
      } else {
        message.error(response.data.message || '提交失败');
      }
    } catch (error) {
      console.error('提交域名过白申请错误:', error);
      message.error(error.response?.data?.message || '提交失败');
    }
  };

  // 查看申请详情
  const viewRequestDetail = (record) => {
    setSelectedRequest(record);
    setDetailModalVisible(true);
  };

  // 重置筛选条件
  const handleResetFilters = () => {
    setSearchTerm('');
    setServerIPFilter('');
    setDateRange(null);
    setPagination({ ...pagination, current: 1 });
  };

  // 执行搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchRequests(1, pagination.pageSize);
  };

  // 表格列定义
  const columns = [
    {
      title: '申请人',
      key: 'applicant',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <div>
            <UserOutlined /> {record.applicant?.username || '未知用户'}
          </div>
          <div style={{ fontSize: 12, color: '#999' }}>
            {record.applicant?.email || '-'}
          </div>
          {record.applicant?.cloudPlatformUID && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              UID: {record.applicant.cloudPlatformUID}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: '服务器IP',
      dataIndex: 'serverIP',
      key: 'serverIP',
      width: 150,
      render: (serverIP) => (
        <Text 
          copyable={{ text: serverIP }}
          style={{ fontFamily: 'monospace' }}
        >
          {serverIP}
        </Text>
      )
    },
    {
      title: '域名数量',
      dataIndex: 'domainCount',
      key: 'domainCount',
      width: 100,
      render: (count, record) => (
        <Tooltip title="点击查看域名列表">
          <Button 
            type="link" 
            onClick={() => viewRequestDetail(record)}
          >
            <Tag color="blue">{count} 个</Tag>
          </Button>
        </Tooltip>
      )
    },
    {
      title: '邮件状态',
      key: 'emailStatus',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {record.emailSent ? (
            <Tag icon={<CheckCircleOutlined />} color="success">
              已发送
            </Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="error">
              发送失败
            </Tag>
          )}
          {record.emailSentAt && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(record.emailSentAt).format('MM-DD HH:mm')}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (createdAt) => (
        <Tooltip title={dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss')}>
          <div>
            <div>{dayjs(createdAt).format('YYYY-MM-DD')}</div>
            <div style={{ fontSize: 12, color: '#999' }}>
              {dayjs(createdAt).format('HH:mm:ss')}
            </div>
          </div>
        </Tooltip>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<GlobalOutlined />}
          onClick={() => viewRequestDetail(record)}
          size="small"
        >
          查看详情
        </Button>
      )
    }
  ];

  // 表格分页变化处理
  const handleTableChange = (newPagination) => {
    fetchRequests(newPagination.current, newPagination.pageSize);
  };

  useEffect(() => {
    fetchRequests();
    fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ padding: 24, maxWidth: 1600, margin: '0 auto' }}>
      {/* 顶部统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总申请数"
              value={statistics.totalRequests}
              prefix={<GlobalOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="邮件成功"
              value={statistics.emailSentCount}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="邮件失败"
              value={statistics.emailFailedCount}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="总域名数"
              value={statistics.totalDomains}
              prefix={<CloudServerOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 筛选工具栏 */}
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Row gutter={16}>
            <Col span={8}>
              <Input
                placeholder="搜索域名"
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onPressEnter={handleSearch}
              />
            </Col>
            <Col span={8}>
              <Input
                placeholder="筛选服务器IP"
                prefix={<CloudServerOutlined />}
                value={serverIPFilter}
                onChange={(e) => setServerIPFilter(e.target.value)}
                onPressEnter={handleSearch}
              />
            </Col>
            <Col span={8}>
              <RangePicker
                style={{ width: '100%' }}
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD"
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Space>
                <Button 
                  type="primary" 
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  搜索
                </Button>
                <Button onClick={handleResetFilters}>
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* 主表格卡片 */}
      <Card
        title={
          <Space>
            <GlobalOutlined style={{ fontSize: 20 }} />
            <span>域名过白申请管理</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<BarChartOutlined />}
              onClick={() => {
                fetchStatistics();
                setStatisticsModalVisible(true);
              }}
            >
              统计分析
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              新增申请
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchRequests();
                fetchStatistics();
              }}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={requests}
          rowKey="_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / 共 ${total} 条`
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 新增申请Modal */}
      <Modal
        title={
          <Space>
            <PlusOutlined />
            <span>提交域名过白申请</span>
          </Space>
        }
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        onOk={() => createForm.submit()}
        okText="提交申请"
        cancelText="取消"
        width={700}
      >
        <Form
          form={createForm}
          onFinish={handleCreateRequest}
          layout="vertical"
        >
          <Form.Item
            label="服务器IP"
            name="serverIP"
            rules={[
              { required: true, message: '请输入服务器IP' },
              { 
                pattern: /^(\d{1,3}\.){3}\d{1,3}$/,
                message: '请输入有效的IP地址'
              }
            ]}
          >
            <Input
              placeholder="请输入服务器IP地址，例如：1.2.3.4"
              prefix={<CloudServerOutlined />}
            />
          </Form.Item>

          <Form.Item
            label="域名列表"
            name="domains"
            rules={[
              { required: true, message: '请输入域名列表' }
            ]}
            extra="每行一个域名，支持用逗号、空格或换行分隔"
          >
            <TextArea
              rows={8}
              placeholder="请输入域名列表，例如：&#10;example.com&#10;test.com&#10;demo.net"
            />
          </Form.Item>
        </Form>

        <Card size="small" style={{ background: '#e6f7ff', border: '1px solid #91d5ff', marginTop: 16 }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Text strong style={{ color: '#1890ff' }}>
              <MailOutlined style={{ marginRight: 4 }} />
              提示说明
            </Text>
            <Text style={{ fontSize: 12 }}>
              • 管理员提交的域名过白申请无需验证IP归属
            </Text>
            <Text style={{ fontSize: 12 }}>
              • 系统将自动发送邮件通知相关人员
            </Text>
            <Text style={{ fontSize: 12 }}>
              • 域名格式将自动验证，请确保输入正确的域名
            </Text>
          </Space>
        </Card>
      </Modal>

      {/* 统计分析Modal */}
      <Modal
        title={
          <Space>
            <BarChartOutlined />
            <span>统计分析</span>
          </Space>
        }
        open={statisticsModalVisible}
        onCancel={() => setStatisticsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setStatisticsModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* 总体统计 */}
          <Card title="总体统计" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="总申请数"
                  value={statistics.totalRequests}
                  prefix={<GlobalOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="总域名数"
                  value={statistics.totalDomains}
                  prefix={<CloudServerOutlined />}
                />
              </Col>
            </Row>
          </Card>

          {/* 邮件发送统计 */}
          <Card title="邮件发送统计" size="small">
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="成功"
                  value={statistics.emailSentCount}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="失败"
                  value={statistics.emailFailedCount}
                  valueStyle={{ color: '#ff4d4f' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="成功率"
                  value={
                    statistics.totalRequests > 0
                      ? ((statistics.emailSentCount / statistics.totalRequests) * 100).toFixed(1)
                      : 0
                  }
                  suffix="%"
                  valueStyle={{ 
                    color: statistics.emailSentCount / statistics.totalRequests >= 0.9 
                      ? '#52c41a' 
                      : '#faad14' 
                  }}
                  prefix={<MailOutlined />}
                />
              </Col>
            </Row>
          </Card>

          {/* 最近7天趋势 */}
          <Card title="最近7天申请趋势" size="small">
            {statistics.dailyTrend && statistics.dailyTrend.length > 0 ? (
              <Table
                dataSource={statistics.dailyTrend}
                rowKey="_id"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: '日期',
                    dataIndex: '_id',
                    key: 'date',
                    render: (date) => dayjs(date).format('YYYY-MM-DD')
                  },
                  {
                    title: '申请次数',
                    dataIndex: 'count',
                    key: 'count',
                    render: (count) => <Tag color="blue">{count}</Tag>
                  },
                  {
                    title: '域名数量',
                    dataIndex: 'domainCount',
                    key: 'domainCount',
                    render: (count) => <Tag color="purple">{count}</Tag>
                  }
                ]}
              />
            ) : (
              <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Space>
      </Modal>

      {/* 申请详情Modal */}
      <Modal
        title={
          <Space>
            <GlobalOutlined />
            <span>申请详情</span>
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedRequest(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedRequest && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* 基本信息 */}
            <Descriptions column={2} bordered>
              <Descriptions.Item label="申请人" span={2}>
                <Space>
                  <UserOutlined />
                  <Text strong>{selectedRequest.applicant?.username || '未知用户'}</Text>
                  <Text type="secondary">({selectedRequest.applicant?.email || '-'})</Text>
                </Space>
              </Descriptions.Item>
              {selectedRequest.applicant?.cloudPlatformUID && (
                <Descriptions.Item label="云平台UID" span={2}>
                  <Text code>{selectedRequest.applicant.cloudPlatformUID}</Text>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="服务器IP">
                <Text 
                  copyable={{ text: selectedRequest.serverIP }}
                  style={{ fontFamily: 'monospace' }}
                >
                  {selectedRequest.serverIP}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="域名数量">
                <Tag color="blue">{selectedRequest.domainCount} 个</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="邮件状态">
                {selectedRequest.emailSent ? (
                  <Tag icon={<CheckCircleOutlined />} color="success">
                    已发送
                  </Tag>
                ) : (
                  <Tag icon={<CloseCircleOutlined />} color="error">
                    发送失败
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="邮件发送时间">
                {selectedRequest.emailSentAt 
                  ? dayjs(selectedRequest.emailSentAt).format('YYYY-MM-DD HH:mm:ss')
                  : '-'
                }
              </Descriptions.Item>
              <Descriptions.Item label="申请时间" span={2}>
                {dayjs(selectedRequest.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>

            {/* 域名列表 */}
            <Card title={`域名列表 (${selectedRequest.domains?.length || 0} 个)`} size="small">
              {selectedRequest.domains && selectedRequest.domains.length > 0 ? (
                <Space wrap>
                  {selectedRequest.domains.map((domain, index) => (
                    <Tag 
                      key={index} 
                      color="blue"
                      style={{ 
                        fontFamily: 'monospace',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(domain);
                        message.success('已复制域名');
                      }}
                    >
                      {domain}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Empty description="无域名" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Card>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default AdminDomainWhitelistManagement;
